/**
 * Advanced Analytics — entity type of a hierarchy node, independent of the
 * word the org uses for it.
 *
 * Node names come from display labels (transformTree -> cleanName(label)). The
 * vehicle node's label is globalConfig.baseEntity.label from the backend
 * ("Bus" when unset, so legacy trees have a node named "bus"; other orgs get
 * "vehicle", "truck", ...). The hierarchy `level` is the reliable source, so it
 * wins for the vehicle node; names are only a fallback. The battery / tms side
 * keeps deriving its type from the node name exactly as before.
 */

export const VEHICLE_ENTITY_TYPE = "vehicle";

// Name the vehicle node had before its label became org-driven.
const LEGACY_VEHICLE_NODE_TYPE = "bus";

const isVehicleLevel = (level) =>
    String(level || "").trim().toLowerCase() === VEHICLE_ENTITY_TYPE;

/**
 * Type derived from a (cleaned) node name: strip spaces and trailing digits,
 * lowercase, and normalize "batterysystem" -> "battery". This is what
 * extractEntities puts on `entity.type` (shown in the chart series count text).
 */
export const inferTypeFromNodeName = (name) => {
    const base = String(name || "").replace(/\s+/g, "").replace(/\d+$/g, "").toLowerCase();
    return base === "batterysystem" ? "battery" : base;
};

/**
 * Canonical entity type of a hierarchy node, for matching against backend
 * entity types (battery / string / pack / module / cell / tms / vehicle).
 * A node at the vehicle level, or a legacy node named "bus", is "vehicle"
 * whatever its display word; everything else falls back to its name.
 */
export const getNodeEntityType = (node, name = node?.name) => {
    if (isVehicleLevel(node?.level)) return VEHICLE_ENTITY_TYPE;
    const type = inferTypeFromNodeName(name);
    return type === LEGACY_VEHICLE_NODE_TYPE ? VEHICLE_ENTITY_TYPE : type;
};

/**
 * Entity type to send to the metrics API for an extracted entity
 * ({ type, level }). Vehicle entities always go out as "vehicle", whether the
 * node carried the vehicle level or the legacy "bus" type; every other entity
 * keeps its `type` unchanged.
 */
export const toApiEntityType = (entity) => {
    if (isVehicleLevel(entity?.level)) return VEHICLE_ENTITY_TYPE;
    const type = entity?.type;
    return String(type || "").toLowerCase() === LEGACY_VEHICLE_NODE_TYPE ? VEHICLE_ENTITY_TYPE : type;
};
